import { NodePositionChange, XYPosition, Node } from '@xyflow/react';
import { useCallback } from 'react';
import { CanvasElementType, CanvasEventType, CanvasMoveGroupEvent } from '../../canvas-types';
import { RfNodeGroup, RfNodeGroupLabel, SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { xYPositionOperation } from '../../helpers/xYPositionOperation';
import { ObjectOperations } from '../objectOperations';
import { MovingNodesOriginalPositionRef, NodeTransformations } from '../useOnNodeChange';
import { useSectionableNodeMoveEvent } from './useSectionableNodeMoveEvent';

const calculateExtraNodePositionChanges = ({
  nodeId,
  diff,
  movingNodes,
  nodeOperations,
  nodeChange,
}: {
  nodeId: string;
  diff: XYPosition;
  movingNodes: React.MutableRefObject<MovingNodesOriginalPositionRef>;
  nodeOperations: ObjectOperations<SweepCanvasRfNode>;
  nodeChange: NodePositionChange;
}) => {
  const node = nodeOperations.getObject(nodeId);
  if (node) {
    if (!movingNodes.current[node.id]) {
      movingNodes.current[node.id] = { originalPosition: node.position };
    }
    const groupPosition = xYPositionOperation(node.position, diff, 'subtract');

    return {
      ...nodeChange,
      id: nodeId,
      position: groupPosition,
    };
  }
};

const getRelatedNodeId = (node: RfNodeGroup | RfNodeGroupLabel): string => {
  return node.type === CanvasElementType.GROUP_LABEL
    ? node.id.replace('label-', '')
    : `label-${node.id}`;
};

export const useOnGroupNodePositionChange = (
  movingNodes: React.MutableRefObject<MovingNodesOriginalPositionRef>,
) => {
  const { createSectionableNodeMoveEvent } = useSectionableNodeMoveEvent();

  const onGroupNodePositionChange = useCallback(
    ({
      nodeChange,
      node,
      nodeTransformations: { extraNodePositionChanges },
      nodeOperations,
      intersectionSectionNode,
    }: {
      nodeChange: NodePositionChange;
      node: RfNodeGroup | RfNodeGroupLabel;
      nodeTransformations: NodeTransformations; // passed by reference for performance
      nodeOperations: ObjectOperations<SweepCanvasRfNode>; // passed by reference for performance
      intersectionSectionNode: Node | null;
    }): CanvasMoveGroupEvent | null => {
      nodeOperations.setObject(node.id, (node) => ({
        ...node,
        expandParent: !nodeChange.dragging,
      }));

      const diff = xYPositionOperation(
        node.position,
        nodeChange.position ?? node.position,
        'subtract',
      );
      const relatedNodeId = getRelatedNodeId(node);

      if (nodeChange.dragging) {
        const extraGroupNodeChange = calculateExtraNodePositionChanges({
          nodeId: relatedNodeId,
          diff,
          movingNodes,
          nodeOperations,
          nodeChange,
        });
        extraGroupNodeChange && extraNodePositionChanges.push(extraGroupNodeChange);
        return null;
      }

      if (node.type === CanvasElementType.GROUP) {
        const moveEvent = createSectionableNodeMoveEvent({
          nodeChange,
          node,
          nodeOperations,
          intersectionSectionNode,
        });
        if (!moveEvent) {
          return null;
        }
        return {
          type: CanvasEventType.MoveGroup,
          nodeId: moveEvent.nodeId,
          newPosition: moveEvent.canvasGridPosition,
          parentId: moveEvent.parentId,
        };
      }
      if (node.type === CanvasElementType.GROUP_LABEL) {
        const extraGroupNodeChange = calculateExtraNodePositionChanges({
          nodeId: relatedNodeId,
          diff,
          movingNodes,
          nodeOperations,
          nodeChange,
        });

        if (!extraGroupNodeChange) {
          return null;
        }

        const moveEvent = createSectionableNodeMoveEvent({
          nodeChange: extraGroupNodeChange,
          node: nodeOperations.getObject(relatedNodeId) as SweepCanvasRfNode,
          nodeOperations,
          intersectionSectionNode,
        });
        if (!moveEvent) {
          return null;
        }
        return {
          type: CanvasEventType.MoveGroup,
          nodeId: moveEvent.nodeId,
          newPosition: moveEvent.canvasGridPosition,
          parentId: moveEvent.parentId,
        };
      }
      return null;
    },
    [createSectionableNodeMoveEvent, movingNodes],
  );

  return {
    onGroupNodePositionChange,
  };
};
